import type { ReactElement, ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { classNames } from '@m1work/shared-utils';

import styles from './Modal.module.scss';

export interface ModalProps {
    open: boolean;
    /** Called on Escape, a click on the backdrop, or `open` turning false. */
    onClose: () => void;
    children?: ReactNode;
    className?: string;
    /** Stacking order; the overlay gets this value, the content one more. */
    zIndex?: number;
    /** Centre the content on the screen. @default true */
    centered?: boolean;
    /** Accessible name of the dialog (translated); not shown, the content has its own heading. */
    title: string;
    /** Escape and a click on the backdrop close the dialog. Turn off for a step the user must finish. @default true */
    isDismissible?: boolean;
}

/**
 * A dialog over the page (Radix Dialog): focus trap, Escape and a backdrop click close it.
 *
 * @example
 * <Modal open onClose={onClose} zIndex={zIndex} title={t('auth.login.title')}>
 *     <LoginForm />
 * </Modal>
 */
export function Modal({
    open,
    onClose,
    children,
    className,
    zIndex,
    centered = true,
    title,
    isDismissible = true,
}: ModalProps): ReactElement {
    return (
        <Dialog.Root
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onClose();
                }
            }}
        >
            <Dialog.Portal>
                <Dialog.Overlay className={styles.overlay} style={{ zIndex }} />
                <Dialog.Content
                    aria-describedby={undefined}
                    className={classNames(styles.content, centered && styles.centered, className)}
                    style={{ zIndex: zIndex !== undefined ? zIndex + 1 : undefined }}
                    onEscapeKeyDown={isDismissible ? undefined : (e) => e.preventDefault()}
                    onMouseDown={(e) => {
                        if (isDismissible && e.target === e.currentTarget) {
                            onClose();
                        }
                    }}
                >
                    <Dialog.Title className={styles['visually-hidden']}>{title}</Dialog.Title>
                    <div>{children}</div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
